<template>
  <MainLayout
    v-scroll="onScroll"
    :bg-color="selectedTab === NAV_VIEWS.INSPIRATION ? 'background-normal' : 'white'"
    no-content-padding
    :no-bottom-content-padding="selectedTab === NAV_VIEWS.REPORTS"
    scroll-toolbar
  >
    <template #toolbar>
      <div class="w-full flex flex-col">
        <div class="relative flex items-center justify-between px-5 py-3.5 border-b border-border-normal">
          <!-- Lens Logo and Route Switcher -->
          <div class="flex items-center">
            <button class="p-1 pr-2 hover:bg-background-normal rounded-md flex items-center gap-2"
            @click="navToLensView">
              <ChevronIcon class="transform rotate-90" />
              <img src="../../assets/icons/lensIcon.png" alt="Lens icon" class="w-8 h-8 rounded-md">
              <BaseText type="label" size="sm" class="text-neutral-900 font-medium">
                Lens
              </BaseText>
            </button>

            <!-- Gap Line -->
            <div class="w-px h-5 bg-border-normal rounded-sm mx-1" />

            <div class="ml-3 flex items-center gap-2">
              <div v-if="!lensRoutes?.length" class="h-5 w-16 skeleton rounded-md" />
              <BaseDropdownRouteSwitcher
                v-else
                :current-route="currentLensRoute"
                :routes="lensRoutes"
              >
                <template #current-icon="{ route }">
                  <div class="mr-2 w-5 h-5 flex-shrink-0">
                    <img v-if="currentLensRoute?.avatar" :src="currentLensRoute.avatar" class="w-5 h-5 rounded-md" alt="Lens avatar">
                    <div v-else class="w-5 h-5 bg-neutral-400 flex items-center justify-center text-background-hover"
                    style="border-radius: 4.5px; font-size: 10px;">
                      {{ currentLensRoute?.initials || '' }}
                    </div>
                  </div>
                </template>
                <template #current-icon-expanded>
                  <div class="mr-2 w-5 h-5 flex-shrink-0">
                    <img v-if="currentLensRoute?.avatar" :src="currentLensRoute.avatar" class="w-5 h-5 rounded-md" alt="Lens avatar">
                    <div v-else class="w-5 h-5 bg-neutral-400 flex items-center justify-center text-background-hover"
                    style="border-radius: 4.5px; font-size: 10px;">
                      {{ currentLensRoute?.initials || '' }}
                    </div>
                  </div>
                </template>
                <template #route-icon="{ route }">
                  <div class="mr-2 w-5 h-5 flex-shrink-0">
                    <img v-if="route?.avatar" :src="route.avatar" class="w-5 h-5 rounded-md" alt="Lens avatar">
                    <div v-else class="w-5 h-5 rounded-md bg-neutral-400 flex items-center justify-center text-background-hover"
                    style="border-radius: 4.5px; font-size: 10px;">
                      {{ route?.initials || '' }}
                    </div>
                  </div>
                </template>
              </BaseDropdownRouteSwitcher>
            </div>
          </div>
          <!-- Data Health Button -->
          <DataHealthButton   
            v-if="lensInsights"
            :last-refreshed-at="lensInsights.last_refreshed_at"
            :date-range="selectedDateRange"
            @refreshComplete="refetchInsights()"
          />
        </div>
        <div class="w-full flex justify-between items-center pr-4"
        :class="{'border-b border-border-normal': selectedTab !== 'inspiration'}">
          <!-- Tab Navigation -->
          <div class="flex gap-2 w-full px-3 py-2">
            <button
              class="brand-view-tab"
              :class="{ 'active': selectedTab === 'dashboard' }"
              @click="selectedTab = 'dashboard'"
            >
              <DashboardIcon class="w-5 h-5" />
              <BaseText size="sm" type="label">
                Dashboard
              </BaseText>
            </button>
            <button
              class="brand-view-tab"
              :class="{ 'active': selectedTab === 'reports' }"
              @click="selectedTab = 'reports'"
            >
              <ReportsIcon class="w-5 h-5" />
              <BaseText size="sm" type="label">
                Reports
              </BaseText>
            </button>
            <button
              class="brand-view-tab"
              :class="{ 'active': selectedTab === 'creative-tests' }"
              @click="selectedTab = 'creative-tests'"
            >
              <LensCreativeTestsIcon class="w-5 h-5" />
              <BaseText size="sm" type="label">
                Creative Tests
              </BaseText>
            </button>
            <button
              class="brand-view-tab"
              :class="{ 'active': selectedTab === 'inspiration' }"
              @click="selectedTab = 'inspiration'"
            >
              <InspirationIcon class="w-5 h-5" />
              <BaseText size="sm" type="label">
                Inspiration
              </BaseText>
            </button>
            <button
              class="brand-view-tab"
              :class="{ 'active': selectedTab === 'comments' }"
              @click="selectedTab = 'comments'"
            >
              <CommentsIcon class="w-5 h-5" />
              <BaseText size="sm" type="label">
                Comments
              </BaseText>
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #filter>
      <div v-if="selectedTab === NAV_VIEWS.DASHBOARD" ref="filterBar"
      class="flex items-center justify-between p-2.5 border-b border-border-normal bg-white bg-opacity-90" 
      style="backdrop-filter: blur(6px); position: sticky; top: 16px; z-index: 40000;">
        <!-- Ad Account Selector -->
        <div class="ad-account-selector-container flex-grow min-w-0">
          <div v-if="!adAccountOptions?.length" class="skeleton w-64 h-9 rounded-md" />
          <AdAccountFilterSelector 
            v-else
            class="fade-in"
            :ad-account-options="adAccountOptions"
            :selected-account-ids="selectedAdAccountIds"
            render-max-selections
            @selection-change="handleAdAccountSelectionChange($event)"
          />
        </div>
        <!-- Date Range Selector -->
        <div class="flex items-start flex-shrink-0">
          <div v-on-clickaway="() => { show.dateRangeSelector = false }" class="relative">
            <transition name="quick-fade">
              <button v-if="selectedTab === NAV_VIEWS.DASHBOARD" class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md flex-shrink-0"
              :class="show.dateRangeSelector ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
              @click="show.dateRangeSelector = !show.dateRangeSelector">
                <CalendarIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  {{ dateFilterLabel }}
                </BaseText>
              </button>
            </transition>
            <transition>
              <div v-if="show.dateRangeSelector" class="date-selector-dropdown">
                <DateRangeSelectorDropdown
                  :selected-date-range.sync="selectedDateRange"
                  :excluded-presets="['allTime']"
                  :min-date="minSelectorDate"
                  is-lens-report
                  close-on-apply
                  @close="show.dateRangeSelector = false"
                />
              </div>
            </transition>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <!-- Comments View - rendered separately from other views -->
      <LensCommentsView
        v-if="selectedTab === NAV_VIEWS.COMMENTS"
        :lens-id="lensId"
        :lens="lens"
        class="w-full"
        style="max-width: 1700px; margin: 0 auto;"
        @showAdDetails="handleDetailsFetch($event)"
      />

      <!-- Loading Spinner (skip for tabs that don't need dashboard data) -->
      <div v-else-if="(loading.dashboard || !hasMounted || getIsLoadingMetrics || (showEmptyData && loading.refetch)) && !['inspiration'].includes(selectedTab)" 
      class="flex items-center justify-center w-full h-full mt-12">
        <BaseLoadingLogo />
      </div>
      <div v-else class="flex items-center justify-center w-full h-full">
        <keep-alive>
          <LensMainView
            v-if="selectedTab === NAV_VIEWS.DASHBOARD && !showEmptyData"
            class="p-5"
            style="min-width: 0px; max-width: 1700px"
            :lens="lens"
            :lens-insights="lensInsights"
            :loading-insights="loading.refetch"
            @changeView="selectedTab = $event"
            @showDetailsDrawer="handleDetailsFetch($event)"
          />
          <LensDashboardReportView
            v-else-if="selectedTab === NAV_VIEWS.REPORTS"
            style="width: 100%; max-width: 1700px"
            :current-lens="lens"
          />
          <LensCreativeTestView
            v-else-if="selectedTab === NAV_VIEWS.CREATIVE_TESTS"
            :lens="lens"
            :ad-account-options="adAccountOptions"
            :selected-account-ids="selectedAdAccountIds"
            @ad-account-selection-change="handleAdAccountSelectionChange($event)"
            @navToDashboard="selectedTab = NAV_VIEWS.DASHBOARD"
          />

          <div v-else-if="showEmptyData && !loading.refetch && selectedTab === NAV_VIEWS.DASHBOARD" class="flex items-center flex-col justify-center w-full h-full text-center gap-y-1 mt-32">
            <div>
              <WarningIcon class="text-icon-normal" :width="32" :height="32" />
            </div>
            <BaseText class="text-text-muted mt-1" type="label" size="sm">
              No spend data found within the time period
            </BaseText>
            <BaseText class="text-text-normal w-64" size="sm">
              Try adjusting your date range
            </BaseText>
            <button class="change-filter-btn flex items-center gap-1.5 mt-4 px-2.5 py-1.5 rounded-md"
            :class="{'active': show.dateRangeSelector}" :disabled="show.dateRangeSelector"
            @click="show.dateRangeSelector = true">
              <CalendarIcon class="text-icon-muted" />
              <BaseText class="text-text-muted" type="label" size="sm">
                {{ dateFilterLabel }}
              </BaseText>
            </button>
          </div>
          <LensInspirationView
            v-else-if="selectedTab === NAV_VIEWS.INSPIRATION"
            :lens-id="lensId"
            style="width: 100%;"
          />
        </keep-alive>
      </div>

      <!-- Modals -->
      <CreateLensReportModal
        v-if="show.createReportModal"
        :lens-id="lensId"
        :init-folder-id="initCreateReportFolderId"
        @close="show.createReportModal = false, initCreateReportFolderId = null"
      />
      <ConfirmDeleteModal
        v-if="show.deleteReportModal"
        delete-title="Are you sure you want to delete this report?"
        delete-description="Once deleted, reports can not be recovered."
        :loading="loading.delete"
        @close="handleDeleteModalView"
        @confirm="handleReportDelete"
      />
      <LensTagModal
        v-if="getShowEditTagModal"
        key="edit-tag-modal"
        @close="SET_SHOW_EDIT_TAG_MODAL(false)"
      />
      <!-- Drawers -->
      <AdInsightsDrawer
        v-if="adDetailsData || loading.details"
        is-ad-level
        :brand="{ ...lens, name: lens?.company_name ?? lens?.name ?? ''}"
        :ad-details-data="adDetailsData"
        :loading="loading.details"
        :date-range="selectedDateRange"
        is-creative-aggregation
        @close="handleCloseDetailsDrawer"
      />
      <!-- Floaters -->
      <!-- <LensDataImportingFloater
        v-if="lensStatus === 'backfill'"
        :import-progress="currentIndex"
      /> -->
    </template>
  </MainLayout>
</template>

<script>
import LensAPI from '@/api/lens'
import MainLayout from '../../layouts/MainLayout.vue'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { parseDateRangeFromQuery, createDateRangeQuery } from '../../utils/lens/filters/filterQueryUtils'
import { formatCustomDateRangeLabel, getMatchingPresetName, getPresetDateRanges, floorDate } from '../../utils/dateUtils'
import { enrichLensAdAccountsWithProviderData, reverseProviderKeyMap } from '../../utils/lens/dataSourceUtils'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import getTabRouteName from '../../utils/lens/dashboardTabRouteMap'
import { mixin as clickaway } from 'vue-clickaway2'
import axios from 'axios'

// Components
import AdAccountFilterSelector from '../../components/lens/AdAccountFilterSelector.vue'
import DateRangeSelectorDropdown from '../../components/search/TopBarComponents/DateRangeSelectorDropdown.vue'
import BaseDropdownRouteSwitcher from '../../components/globals/BaseDropdownRouteSwitcher.vue'
import LensDashboardReportView from './LensDashboardReportView.vue'
import LensMainView from './LensMainView.vue'
import AdInsightsDrawer from '../../components/lens/report/AdInsightsDrawer.vue'
import LensCreativeTestView from './LensCreativeTestView.vue'
import LensDataImportingFloater from '../../components/lens/LensDataImportingFloater.vue'
import LensInspirationView from './LensInspirationView.vue'
import LensCommentsView from './LensCommentsView.vue'
import DataHealthButton from '../../components/lens/DataHealthButton.vue'

// Modals
import CreateLensReportModal from '../../components/lens/CreateLensReportModal.vue'
import ConfirmDeleteModal from '../../components/globals/ConfirmDeleteModal.vue'
import LensTagModal from '../../components/lens/LensTagModal.vue'

// Icons
import KebabMenuIcon from '../../components/globals/Icons/KebabMenuIcon.vue'
import ChangeFolderIcon from '../../components/globals/Icons/ChangeFolderIcon.vue'
import DeleteIcon from '../../components/globals/Icons/DeleteIcon.vue'
import BarGraphIcon from '../../components/globals/Icons/LensIcons/graphs/BarGraphIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import RefreshIcon from '../../components/globals/Icons/LensIcons/RefreshIcon.vue'
import CalendarIcon from '../../components/globals/Icons/CalendarIcon.vue'
import TablePagination from '../../components/lens/table/TablePagination.vue'
import WarningIcon from '../../components/globals/Icons/WarningIcon.vue'

// Tab Icons
import DashboardIcon from '../../components/globals/Icons/LensBrandIcons/DashboardIcon.vue'
import ReportsIcon from '../../components/globals/Icons/LensBrandIcons/ReportsIcon.vue'
import LensCreativeTestsIcon from '../../components/globals/Icons/LensBrandIcons/LensCreativeTestsIcon.vue'
import InspirationIcon from '../../components/globals/Icons/LensBrandIcons/InspirationIcon.vue'
import CommentsIcon from '../../components/globals/Icons/LensBrandIcons/CommentsIcon.vue'

export default {
  name: 'LensDashboardView',
  components: {
    MainLayout,
    CreateLensReportModal,
    ConfirmDeleteModal,
    LensTagModal,
    BaseDropdownRouteSwitcher,
    LensMainView,
    LensDashboardReportView,
    DateRangeSelectorDropdown,
    AdAccountFilterSelector,
    AdInsightsDrawer,
    LensCreativeTestView,
    LensDataImportingFloater,
    LensInspirationView,
    LensCommentsView,
    DataHealthButton,

    // Icons
    KebabMenuIcon,
    DeleteIcon,
    ChangeFolderIcon,
    BarGraphIcon,
    ChevronIcon,
    RefreshIcon,
    WarningIcon,
    CalendarIcon,
    TablePagination,
    DashboardIcon,
    ReportsIcon,
    LensCreativeTestsIcon,
    InspirationIcon,
    CommentsIcon
  },
  mixins: [clickaway],
  data () {
    const headers = [
      { label: 'Report Name', span: 4 },
      { label: 'Created By', span: 4 },
      { label: 'Last Updated', span: 3 }
    ]
    const NAV_VIEWS = {
      DASHBOARD: 'dashboard',
      CREATIVE_TESTS: 'creative-tests',
      REPORTS: 'reports',
      INSPIRATION: 'inspiration',
      COMMENTS: 'comments'
    }
    const subNavRoutes = [
      { label: 'Dashboard', name: 'LensDashboard', key: 'dashboard' },
      { label: 'Reports', name: 'LensReports', key: 'reports' },
      { label: 'Creative Tests', name: 'LensCreativeTests', key: 'creative-tests' },
      { label: 'Inspiration', name: 'LensInspiration', key: 'inspiration' },
      { label: 'Comments', name: 'LensComments', key: 'comments' }
    ]

    return {
      // Constants
      headers,
      NAV_VIEWS,
      subNavRoutes,

      // Data States
      selectedTab: 'dashboard',
      lens: null,
      lensId: this.$route.params.lensId || null,
      lensInsights: null,
      adDetailsData: null,
      adAccounts: [],
      selectedAdAccountIds: [],

      reportToDelete: null,
      initCreateReportFolderId: null,
      
      // Date Range Selector
      selectedDateRange: {
        start: null,
        end: null,
        isRelativeToToday: true
      },
      dateFilterLabel: 'Last 7 days',

      // UI States
      hasMounted: false,
      loading: {
        delete: false,
        dashboard: true,
        refetch: false,
        details: false,
        reports: true
      },
      show: {
        createReportModal: false,
        deleteReportModal: false,
        dateRangeSelector: false
      },

      // Lens Status
      // lensStatus: null,
      // backfillDays: null,
      // currentIndex: null,
    }
  },
  watch: {
    selectedDateRange (newVal) {
      if (!newVal?.start || !newVal?.end) {
        this.dateFilterLabel = 'All Time'
      } else if (newVal.isRelativeToToday) {
        this.dateFilterLabel = getMatchingPresetName(newVal) || formatCustomDateRangeLabel(newVal.start, newVal.end)
      } else {
        this.dateFilterLabel = formatCustomDateRangeLabel(newVal.start, newVal.end)
      }
      if (this.$route.params.lensId) {
        localStorage.setItem(`lens__dashboard_${this.$route.params.lensId}_date_range`, JSON.stringify(createDateRangeQuery(newVal)))
      }
      this.refetchInsights({ dateRange: newVal })
    },
    getShowReportCreationModal (newValue) {
      if (newValue) {
        this.selectedTab = 'reports'
        this.show.createReportModal = true
        this.initCreateReportFolderId = newValue.length ? newValue : null
        this.SET_SHOW_REPORT_CREATION_MODAL(null)
      }
    },
    selectedTab (newVal) {
      const route = newVal === 'dashboard' ? '' : newVal
      const url = `/lens/${this.lensId}/${route}`
      window.history.replaceState({}, '', url)
    }
  },
  created () {
    // Check selected tab
    const tab = this.$route.meta.tab
    console.log('[LensDashboard] Route tab from meta:', tab)
    if (tab) {
      this.selectedTab = tab || 'dashboard'
    }
    // Initialize the date range (use previous session if available)
    const existingDateRange = this.$route.params.lensId ? localStorage.getItem(`lens__dashboard_${this.$route.params.lensId}_date_range`) : null
    if (existingDateRange) {
      this.selectedDateRange = parseDateRangeFromQuery(JSON.parse(existingDateRange))
    } else {
      // Default to last 7 days
      const last7Days = getPresetDateRanges()['last7Days']
      this.selectedDateRange = {
        start: last7Days.start,
        end: last7Days.end,
        isRelativeToToday: true
      }
    }
  },
  async mounted () {
    if (!this.getPresetsLoaded) {
      await this.fetchPresets()
    }

    // Get lens status
    // await this.getLensStatus(this.lensId)

    // Get lens details
    await this.fetchLens()

    // Initialize the ad account ids filter
    const selectedAdAccountIds = this.initializeAdAccounts()

    // This should be pulled out from the base of this view (It will load on every view)
    await this.fetchInsights({ adAccountIds: selectedAdAccountIds })
    this.loading.dashboard = false

    // Check for report creation modal
    if (this.getShowReportCreationModal) {
      this.show.createReportModal = true
      this.initCreateReportFolderId = this.getShowReportCreationModal.length ? this.getShowReportCreationModal : null
      this.SET_SHOW_REPORT_CREATION_MODAL(null)
    }

    // Configure event listeners (respond to ad accounts being connected/disconnected via LensAdAccountConnector)
    EventBus.$on('lens:connected-ad-accounts', async () => {
      await this.fetchLens()
      this.refetchInsights()
    })
    EventBus.$on('lens:removed-ad-account', async () => {
      await this.fetchLens()
      // On removal, we need to reinitialize the ad accounts filter (in case we removed a dependency)
      const newAdAccountIds = this.initializeAdAccounts()
      this.refetchInsights({ adAccountIds: newAdAccountIds })
    })

    this.hasMounted = true
  },
  computed: {
    ...mapGetters('LensModule', ['getShowReportCreationModal', 'getSidebarLenses', 'getProviders', 'getIsLoadingSidebarLenses', 'getShowEditTagModal']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getIsLoadingMetrics', 'getMetricLookup', 'getPresets', 'getPresetsLoaded']),
    adAccountOptions () {
      if (!this.getProviders?.length || !this.lens?.ad_accounts?.length) return null
      return enrichLensAdAccountsWithProviderData(this.getProviders, this.lens.ad_accounts)
    },
    lensRoutes () {
      if (this.getIsLoadingSidebarLenses || !this.getSidebarLenses?.length) return []
      return this.getSidebarLenses.map(lens => ({
        name: lens.name,
        routeName: getTabRouteName(this.selectedTab),
        params: { lensId: lens.id },
        avatar: lens?.avatar || null,
        initials: this.getInitials(lens.name)
      }))
    },
    currentLensRoute () {
      return this.lensRoutes.find(route => route.params.lensId === this.lensId)
    },
    showEmptyData () {
      return !this.lensInsights?.insights
    },
    minSelectorDate () {
      const minDateStr = this.lens?.metadata?.min_event_date // YYYY-MM-DD
      return minDateStr
        ? floorDate(new Date(minDateStr))
        : floorDate(new Date(new Date().setDate(new Date().getDate() - 365))) // Default to 365 days ago
    }
  },
  methods: {
    ...mapMutations('LensModule', ['SET_SHOW_REPORT_CREATION_MODAL', 'SET_SHOW_EDIT_TAG_MODAL']),
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    ...mapActions('MetricsModule', ['fetchPresets', 'setMetricsPlatform']),
    // async getLensStatus (lensId) {
    //   const { data } = await LensAPI.Lens.getLensStatus(lensId)

    //   this.lensStatus = data.triple_whale_accounts[0]?.status
    //   this.backfillDays = data.triple_whale_accounts[0]?.total
    //   this.currentIndex = data.triple_whale_accounts[0]?.currentIndex
    // },
    async fetchLens () {
      try {
        const { data } = await LensAPI.Lens.get(this.lensId)
        this.lens = data
      } catch (error) {
        console.error('Error fetching lens details:', error)
        this.$showAlert({
          type: 'error',
          message: 'Error fetching Lens Brand. See console for details.'
        })
      }
    },
    initializeAdAccounts () {
      const platform = this.getMetricsPlatform
      const connectedAdAccounts = this.lens.ad_accounts
      this.adAccounts = connectedAdAccounts

      // If we have stored ad account ids from a previous session, use them
      const storedAdAccountIds = localStorage.getItem(`lens__dashboard_${this.$route.params.lensId}_ad_account_ids`)
      if (storedAdAccountIds) {
        const { accountIds, platform } = JSON.parse(storedAdAccountIds)
        // Check that none of the stored ad account ids were disconnected from the lens brand
        if (accountIds.every(id => connectedAdAccounts.some(account => account.account_id === id))) {
          this.setMetricsPlatform(platform)
          this.selectedAdAccountIds = accountIds
          return accountIds
        }
      }

      // Set up the initial filter based on current metrics platform.
      const platformAdAccounts = connectedAdAccounts.filter(account => reverseProviderKeyMap[account.provider] === platform)
      let selectedAdAccountIds = []
      if (platformAdAccounts.length) {
        selectedAdAccountIds = platformAdAccounts.map(account => account.account_id)
      } else if (connectedAdAccounts.length) {
        // If there are no ad accounts for the current platform, switch to the next available platform
        const firstAccount = connectedAdAccounts[0]
        this.setMetricsPlatform(reverseProviderKeyMap[firstAccount.provider])
        selectedAdAccountIds = connectedAdAccounts.filter(account => account.provider === firstAccount.provider).map(account => account.account_id)
      } else {
        this.$showAlert({
          type: 'error',
          message: 'This lens brand does not have any ad accounts.'
        })
      }
      this.selectedAdAccountIds = selectedAdAccountIds
      return selectedAdAccountIds
    },
    async fetchInsights (query = {}) {
      const { dateRange = this.selectedDateRange, adAccountIds = this.selectedAdAccountIds } = query
      try {
        const dateQuery = createDateRangeQuery(dateRange)
        const attrWindow = {
          click: this.lens.click_attribution_window,
          view: this.lens.view_attribution_window
        }
        const insightRes = await LensAPI.Lens.getInsightByLens(this.lensId, {
          platform: this.getMetricsPlatform,
          adAccountIds: adAccountIds,
          dateQuery,
          attrWindow,
          allowCancel: true
        })
        this.lensInsights = insightRes.data
        return false
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('New query detected: Prev request canceled')
          return true // return wasCancelled = true
        }
        console.error('Error fetching insights:', error)
        this.$showAlert({
          type: 'error',
          message: 'An error occurred while fetching insights. See console for details.'
        })
        return false
      }
    },
    async refetchInsights (query = {}) {
      if (!this.lens) return
      this.loading.refetch = true
      const wasCancelled = await this.fetchInsights(query)
      if (!wasCancelled) this.loading.refetch = false
    },
    handleAdAccountSelectionChange (selection) {
      const { accountIds, platform } = selection
      this.setMetricsPlatform(platform)
      this.selectedAdAccountIds = accountIds
      if (this.$route.params.lensId) {
        localStorage.setItem(`lens__dashboard_${this.$route.params.lensId}_ad_account_ids`, JSON.stringify({
          accountIds,
          platform
        }))
      }
      this.refetchInsights({ adAccountIds: accountIds })
    },
    async handleDetailsFetch (ad) {
      this.loading.details = true
      const dateFilter = createDateRangeQuery(this.selectedDateRange)
      const groupPayload = {
        adAccountIds: this.selectedAdAccountIds,
        platform: this.getMetricsPlatform,
        currentWindow: {
          click: this.lens.click_attribution_window,
          view: this.lens.view_attribution_window
        },
        filters: dateFilter,
        selectedColumns: Object.keys(this.getMetricLookup).map(key => ({ key, is_pinned: false }))
      }
      try {
        let res

        if (ad.creative_id || ad.creative_hash) {
          const idPayload = {
            lensId: this.lensId,
            creativeHash: ad?.creative_hash,
            adId: ad.ad_id
          }
          res = await LensAPI.AdGroups.getLensCreativeGroup(idPayload, groupPayload)
        } else {
          res = await LensAPI.AdGroups.getAdDetailsByAdId(this.lensId, ad.ad_id, {
            click: this.lens.click_attribution_window,
            view: this.lens.view_attribution_window
          })
        }

        console.log('[LensDashboard] API response:', res)

        // Handle both response structures - res.data or res directly
        const adData = res?.data || res
        // The insights drawer identifies the ad it's rendering through these ids (header, account info & meta links)
        this.adDetailsData = {
          ...ad,
          ...adData,
          account_id: ad.account_id ?? adData?.account_id,
          aiTransaction: adData?.ai_transaction,
          creativeTags: adData?.custom_tags ?? [],
          detailsQuery: {
            groupBy: 'creative',
            query: groupPayload,
            date: this.selectedDateRange,
          }
        }
        console.log('[LensDashboard] adDetailsData set:', this.adDetailsData)
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.details = false
      }
    },
    handleCloseDetailsDrawer () {
      this.adDetailsData = null
    },
    async handleReportDelete () {
      const reportId = this.reportToDelete
      this.loading.delete = true
      console.log('%c Deleting Report', 'color: pink; font-size: 14px;')
      try {
        await LensAPI.LensReport.remove(reportId)
        await this.fetchSidebarLenses()
      } catch (e) {
        this.$showAlert({
          type: 'error',
          message: 'Deleting report error'
        })
      } finally {
        this.loading.delete = false
        this.handleDeleteModalView()
        this.$showAlert({
          type: 'success',
          message: 'Report successfully deleted'
        })
      }
    },

    // ========== UI METHODS ==========

    handleDeleteModalView (reportId) {
      this.reportToDelete = reportId
      this.show.deleteReportModal = !!reportId
    },
    navToReport (reportId) {
      this.$router.push({ name: 'LensReport', params: { lensId: this.lensId, reportId } })
    },
    navToLensView () {
      this.$router.push({ name: 'LensView' })
    },
    formatDate (dateMS) {
      const date = new Date(dateMS)
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return date.toLocaleDateString('en-US', options)
    },
    getInitials (name) {
      if (!name) return ''
      const initials = name.split(' ').map(word => word[0]).join('').toUpperCase()
      return initials.substring(0, 2) // Limit to 2 characters
    },
    onScroll (_, position) {
      // Find the filter bar using a selector since it's in a child component
      const filterBar = this.$refs.filterBar ?? document.querySelector('.lens-inspiration-container .filter-bar')
      if (filterBar) {
        const marginValue = Math.min(position.scrollTop / 6, 16)
        const rounding = Math.min(position.scrollTop / 10, 8)

        filterBar.style.marginLeft = `${marginValue}px`
        filterBar.style.marginRight = `${marginValue}px`
        filterBar.style.marginTop = `${marginValue}px`
        filterBar.style.borderRadius = `${rounding}px`

        const shadowSection = (maxValue) => {
          return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
        }

        if (position.scrollTop) {
          filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(15)}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(7)}px ${shadowSection(10)}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(7)}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(3)}px ${shadowSection(5)}px`
        } else {
          filterBar.style.boxShadow = null
        }
      }
    }
  }
}
</script>

<style scoped>
.option-enter-active,
.option-leave-active {
  transition: opacity 100ms ease-in-out;
}

.option-enter-from,
.option-enter,
.option-leave-to {
  opacity: 0;
}

.option-enter-to,
.option-leave-from {
  opacity: 1;
}

.option-menu-button {
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}

.option-menu-button.active {
  background-color: #ECEFF3;
}

.option-menu-button.inactive:hover {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.report-options {
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
  width: 105px;
  z-index: 50;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.brand-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678;
  /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}

.brand-view-tab:hover {
  color: #303546;
  /* text-text-muted */
  background-color: #F6F8FA;
  /* bg-neutral-25 */
}

.brand-view-tab.active {
  color: #303546;
  /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}

.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}

.data-health-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
}
.data-health-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.17), 0 0 0 1px rgba(0, 56, 108, 0.12);
}

.change-filter-btn {
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.06), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.06);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.change-filter-btn:hover {
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.08), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.12), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.12);
}
.change-filter-btn.active, .change-filter-btn.active:hover {
  background-color: #F6F8FA;
  box-shadow: none;
  cursor: default;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.quick-fade-enter-active, .quick-fade-leave-active {
  transition: opacity 60ms ease-in-out;
}

.v-enter-from, .v-enter, .v-leave-to, .quick-fade-enter-from, .quick-fade-enter, .quick-fade-leave-to {
  opacity: 0;
}

.v-enter-to, .v-leave-from, .quick-fade-enter-to, .quick-fade-leave-from {
  opacity: 1;
}


.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
